import { colorForUserId } from './awareness-color';

function readSelection(diagramState) {
    return {
        focusedIds:   diagramState.focusedIds   || [],
        insideElement: diagramState.focusedInsideElement || null,
    };
}

function selectionsEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.focusedIds.length !== b.focusedIds.length) return false;
    for (let i = 0; i < a.focusedIds.length; i++) {
        if (a.focusedIds[i] !== b.focusedIds[i]) return false;
    }
    const ai = a.insideElement;
    const bi = b.insideElement;
    if (!ai !== !bi) return false;
    if (ai && (ai.elementId !== bi.elementId || ai.insideElementId !== bi.insideElementId)) {
        return false;
    }
    return true;
}

export function setupAwarenessProducer(awareness, store) {
    let destroyed = false;

    const user = store.getState().auth?.currentUser;
    let lastSelection = readSelection(store.getState().diagram || {});

    const initialState = {
        selection: lastSelection,
        cursor: null,
    };
    if (user) {
        initialState.user = {
            id: user.id,
            name: user.name || 'Unknown',
            image: user.image || null,
            color: colorForUserId(user.id),
        };
    }
    awareness.setLocalState(initialState);

    const unsubscribe = store.subscribe(() => {
        if (destroyed) return;
        const next = readSelection(store.getState().diagram || {});
        if (selectionsEqual(next, lastSelection)) return;
        lastSelection = next;
        awareness.setLocalStateField('selection', next);
    });

    let canvasEl = null;
    let currentViewportToDiagram = null;
    let pendingEvent = null;
    let rafId = null;
    let lastCursor = null;

    function flushCursor() {
        rafId = null;
        if (!pendingEvent || destroyed || !currentViewportToDiagram) return;
        const { clientX, clientY } = pendingEvent;
        pendingEvent = null;
        const { x, y } = currentViewportToDiagram(clientX, clientY);
        lastCursor = { x, y };
        awareness.setLocalStateField('cursor', lastCursor);
    }

    function onMousemove(e) {
        pendingEvent = e;
        if (rafId == null) {
            rafId = requestAnimationFrame(flushCursor);
        }
    }

    function onMouseleave() {
        if (rafId != null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
        pendingEvent = null;
        // Keep cursor at last known position — do not clear it here.
        // Cursor is only cleared when the tab goes hidden or the canvas is detached.
    }

    function onVisibilityChange() {
        if (destroyed) return;
        if (document.hidden) {
            if (rafId != null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            pendingEvent = null;
            awareness.setLocalStateField('cursor', null);
        } else if (lastCursor) {
            awareness.setLocalStateField('cursor', lastCursor);
        }
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    function _detachCanvas() {
        if (rafId != null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
        pendingEvent = null;
        lastCursor = null;
        if (canvasEl) {
            canvasEl.removeEventListener('mousemove', onMousemove);
            canvasEl.removeEventListener('mouseleave', onMouseleave);
            canvasEl = null;
            currentViewportToDiagram = null;
            awareness.setLocalStateField('cursor', null);
        }
    }

    function detachCanvas() {
        if (destroyed) return;
        _detachCanvas();
    }

    function attachCanvas(el, viewportToDiagram) {
        if (destroyed) return;
        _detachCanvas();
        canvasEl = el;
        currentViewportToDiagram = viewportToDiagram;
        el.addEventListener('mousemove', onMousemove);
        el.addEventListener('mouseleave', onMouseleave);
    }

    function destroy() {
        if (destroyed) return;
        destroyed = true;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        _detachCanvas();
        unsubscribe();
        awareness.setLocalState({});
    }

    return { attachCanvas, detachCanvas, destroy };
}
